/**
 * Cash flow (KAN-49 / KAN-89 / KAN-90).
 *
 * Three things on one page, in the order Finance asks them: how much is there,
 * where it goes, and what has to be paid out of it.
 *
 * The design decision that shapes everything else is that **the forecast is a
 * band, not a line**. A single projected balance implies a precision this model
 * does not have, and the first time it is wrong the whole screen loses
 * credibility rather than the assumption that deserved to. So the chart draws
 * three series, the shortfall is called on the optimistic one, and the
 * assumptions behind every figure are printed underneath rather than being left
 * in a service comment nobody reads.
 */
import { lazy, Suspense, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CASH_HORIZONS,
  DATE_ADJUSTMENTS,
  DATE_ADJUSTMENT_LABELS,
  OBLIGATION_FREQUENCIES,
  OBLIGATION_FREQUENCY_LABELS,
  OBLIGATION_KINDS,
  OBLIGATION_KIND_LABELS,
  formatBps,
  type CashForecast,
  type CashHorizon,
  type DateAdjustment,
  type ObligationConfidence,
  type ObligationFrequency,
  type ObligationKind,
  type PayableRanking,
} from '@accounting/shared'
import { apiFetch } from '../lib/api'
import { useSession } from '../lib/session'
import { formatDate, formatDateTime, formatMoney } from '../lib/format'
import { Alert } from '../ui/Alert'
import { Badge } from '../ui/Badge'
import { Button } from '../ui/Button'
import { Card, CardBody, CardHeader } from '../ui/Card'
import { PageHeader } from '../ui/PageHeader'
import { Stat, StatRow } from '../ui/Stat'
import { EmptyRow, TableWrap, Td, Th } from '../ui/Table'

/**
 * Lazily loaded for the reason the executive trends are: Recharts is a large
 * share of this bundle and draws nothing above the fold. The headline figures
 * and the shortfall warning must not wait on a charting library to parse.
 */
const CashFlowChart = lazy(() => import('./CashFlowChart'))

interface Obligation {
  id: string
  name: string
  kind: ObligationKind
  frequency: ObligationFrequency
  confidence: ObligationConfidence
  amountMinor: number
  startsOn: string
  endsOn: string | null
  semiMonthlyDays: [number, number] | null
  dateAdjustment: DateAdjustment
  notes: string | null
  isActive: boolean
  upcoming: { date: string; scheduledDate: string; amountMinor: number }[]
}

interface ForecastResponse {
  forecast: CashForecast
  payables: PayableRanking[]
}

export function CashFlow() {
  const { may } = useSession()
  const [horizon, setHorizon] = useState<CashHorizon>(30)

  const forecast = useQuery({
    queryKey: ['cash-flow', horizon],
    queryFn: () => apiFetch<ForecastResponse>(`/cash-flow?horizon=${horizon}`),
  })

  const obligations = useQuery({
    queryKey: ['cash-flow', 'obligations'],
    queryFn: () => apiFetch<Obligation[]>('/cash-flow/obligations'),
  })

  return (
    <>
      <PageHeader
        title="Cash flow"
        description="What the firm holds, what is expected in and out, and which bills are at risk against it. Nothing on this screen moves money."
        actions={
          <div className="flex items-center gap-1 rounded-md border border-ink-300 bg-white p-0.5">
            {CASH_HORIZONS.map((days) => (
              <Button
                key={days}
                size="sm"
                variant={days === horizon ? 'primary' : 'ghost'}
                onClick={() => setHorizon(days)}
              >
                {days} days
              </Button>
            ))}
          </div>
        }
      />

      {forecast.isPending && <p className="text-sm text-ink-600">Loading the forecast…</p>}

      {forecast.isError && (
        <Alert tone="error" title="The forecast could not be loaded">
          {(forecast.error as Error).message}
        </Alert>
      )}

      {forecast.data && (
        <div className="space-y-6">
          <Position forecast={forecast.data.forecast} />
          <Projection forecast={forecast.data.forecast} />
          <Payables rows={forecast.data.payables} />
          <Assumptions forecast={forecast.data.forecast} />
          <Obligations
            obligations={obligations.data ?? []}
            loading={obligations.isPending}
            canManage={may('cash_flow.manage')}
          />
          <Accuracy />
        </div>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------ *
 * The position
 * ------------------------------------------------------------------------ */

function Position({ forecast }: { forecast: CashForecast }) {
  const { position } = forecast

  if (!position.available) {
    return (
      <Alert tone="warning" title="The opening balance is unknown">
        <p>{position.unavailableReason}</p>
        <p className="mt-2">
          The movements below are still real — every invoice, bill and obligation is there. What is
          missing is the level they are measured from, so no running balance and no shortfall is
          shown rather than one assumed from zero.
        </p>
        {/**
         * The one figure that is known when the operating balance is not, shown
         * because the commonest reason for being here is that every bank
         * account was held out as client money — and a screen with no number on
         * it at all reads as a system fault rather than as the specific,
         * fixable thing it is.
         */}
        {position.trustAccounts.length > 0 && (
          <p className="mt-2">
            Client trust held, which is known and is never the firm's cash:{' '}
            {formatMoney(position.trustBalanceMinor)} across {position.trustAccounts.length} account
            {position.trustAccounts.length === 1 ? '' : 's'}.
          </p>
        )}
      </Alert>
    )
  }

  const closing = forecast.days[forecast.days.length - 1]

  return (
    <>
      <StatRow>
        <Stat
          label="Operating cash"
          value={formatMoney(position.operatingBalanceMinor)}
          hint={
            <>
              Across {position.accounts.length} account
              {position.accounts.length === 1 ? '' : 's'}, as reconciled in QuickBooks
              {position.balanceAsOf ? ` on ${formatDate(position.balanceAsOf)}` : ''}.
              {position.accountsWithoutBalance > 0 && (
                <>
                  {' '}
                  {position.accountsWithoutBalance} account
                  {position.accountsWithoutBalance === 1 ? '' : 's'} reported no balance and{' '}
                  {position.accountsWithoutBalance === 1 ? 'is' : 'are'} not in this figure.
                </>
              )}
            </>
          }
        />
        <Stat
          label={`Projected in ${forecast.horizonDays} days`}
          value={formatMoney(closing?.closingBalanceMinor.expected ?? null)}
          hint={
            closing
              ? `Between ${formatMoney(closing.closingBalanceMinor.conservative)} and ${formatMoney(closing.closingBalanceMinor.optimistic)}.`
              : undefined
          }
        />
        <Stat
          label="First shortfall"
          tone={forecast.firstShortfallDate ? 'warning' : 'muted'}
          value={
            forecast.firstShortfallDate ? formatDate(forecast.firstShortfallDate) : 'None projected'
          }
          hint={
            forecast.firstShortfallDate
              ? 'The first day even the optimistic reading closes below the floor.'
              : `No day in the next ${forecast.horizonDays} closes below the floor, on any reading.`
          }
        />
        <Stat
          label="Client trust held"
          value={formatMoney(position.trustBalanceMinor)}
          tone="muted"
          hint="Shown so the exclusion can be checked. Never added to operating cash — it is client money the firm may not spend."
        />
      </StatRow>

      {forecast.firstShortfallDate && (
        <Alert tone="warning" title={`Cash is projected short on ${formatDate(forecast.firstShortfallDate)}`}>
          Even on the optimistic collection assumptions, the balance closes below the floor that
          day. The bills ranked below are the ones to look at first.
        </Alert>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------ *
 * The projection
 * ------------------------------------------------------------------------ */

function Projection({ forecast }: { forecast: CashForecast }) {
  return (
    <Card>
      <CardHeader
        title="Projected balance"
        description="Three readings, not one. Only the collections vary between them — an obligation is a date the firm has already committed to, and discounting it to make the optimistic case look better would be a fiction."
      />
      <CardBody>
        <Suspense
          fallback={<p className="text-sm text-ink-600">Loading the chart…</p>}
        >
          <CashFlowChart forecast={forecast} />
        </Suspense>
      </CardBody>
    </Card>
  )
}

/* ------------------------------------------------------------------------ *
 * Payment prioritisation
 * ------------------------------------------------------------------------ */

const CRITICALITY_TONE = {
  critical: 'critical',
  normal: 'neutral',
  deferrable: 'brand',
} as const

function Payables({ rows }: { rows: PayableRanking[] }) {
  return (
    <Card>
      <CardHeader
        title="Pending bills, against projected cash"
        description="Ordered by criticality, then due date, then amount — an ordering rather than a score, because a number assembled from invented weights reads as authoritative and cannot be checked. Nothing here pays anything."
      />
      <TableWrap>
        <thead>
          <tr>
            <Th>Bill</Th>
            <Th>Vendor</Th>
            <Th className="text-right">Amount</Th>
            <Th>Due</Th>
            <Th>Criticality</Th>
            <Th className="text-right">Cash projected that day</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && <EmptyRow colSpan={6}>No bills are outstanding.</EmptyRow>}
          {rows.map((row) => (
            <tr key={row.billId} className={row.atRisk ? 'bg-accent-50/60' : undefined}>
              <Td>
                {row.label}
                {row.atRisk && (
                  <span className="ml-2">
                    <Badge tone="warning">At risk</Badge>
                  </span>
                )}
              </Td>
              <Td>{row.vendorName}</Td>
              <Td className="text-right tabular-nums">{formatMoney(row.amountMinor)}</Td>
              <Td>
                {formatDate(row.dueAt, 'No due date')}
                {row.daysOverdue !== null && row.daysOverdue > 0 && (
                  <span className="ml-2 text-xs text-accent-700">
                    {row.daysOverdue}d overdue
                  </span>
                )}
              </Td>
              <Td>
                <Badge tone={CRITICALITY_TONE[row.criticality]}>{row.criticality}</Badge>
              </Td>
              <Td className="text-right tabular-nums">
                {row.projectedCashOnDueDateMinor === null
                  ? '—'
                  : formatMoney(row.projectedCashOnDueDateMinor)}
              </Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
    </Card>
  )
}

/* ------------------------------------------------------------------------ *
 * Assumptions
 * ------------------------------------------------------------------------ */

/**
 * Printed on the page rather than buried in a tooltip.
 *
 * KAN-90 asks for a forecast that states its assumptions, and the reason is
 * that this is the screen somebody screenshots into an email. A caveat that
 * only exists on hover does not survive that.
 */
function Assumptions({ forecast }: { forecast: CashForecast }) {
  return (
    <Card>
      <CardHeader
        title="What this assumes"
        description={`Generated ${formatDateTime(forecast.generatedAt)}, covering ${formatDate(forecast.from)} to ${formatDate(forecast.to)}.`}
      />
      <CardBody>
        <ul className="space-y-2 text-sm text-ink-700">
          {forecast.assumptions.map((assumption) => (
            <li key={assumption} className="flex gap-2">
              <span aria-hidden className="text-ink-400">
                •
              </span>
              <span>{assumption}</span>
            </li>
          ))}
        </ul>
      </CardBody>
    </Card>
  )
}

/* ------------------------------------------------------------------------ *
 * Obligations
 * ------------------------------------------------------------------------ */

function Obligations({
  obligations,
  loading,
  canManage,
}: {
  obligations: Obligation[]
  loading: boolean
  canManage: boolean
}) {
  const [adding, setAdding] = useState(false)

  return (
    <Card>
      <CardHeader
        title="Recurring obligations"
        description="Payroll, taxes, rent and every other scheduled outflow, as rules rather than dated payments. The dates below are generated by the same code the forecast uses, so a preview and the chart cannot disagree."
        actions={
          canManage && (
            <Button variant="accent" size="sm" onClick={() => setAdding((open) => !open)}>
              {adding ? 'Cancel' : 'Add obligation'}
            </Button>
          )
        }
      />

      {adding && <ObligationForm onDone={() => setAdding(false)} />}

      <TableWrap>
        <thead>
          <tr>
            <Th>Name</Th>
            <Th>Kind</Th>
            <Th>Frequency</Th>
            <Th className="text-right">Amount</Th>
            <Th>Next dates</Th>
            <Th>Certainty</Th>
          </tr>
        </thead>
        <tbody>
          {loading && <EmptyRow colSpan={6}>Loading…</EmptyRow>}
          {!loading && obligations.length === 0 && (
            <EmptyRow colSpan={6}>
              No obligations are recorded yet. Payroll and taxes are the largest and least flexible
              outflows a firm has — until they are here, the forecast understates what has to leave.
            </EmptyRow>
          )}
          {obligations.map((obligation) => (
            <tr key={obligation.id}>
              <Td>
                {obligation.name}
                {obligation.notes && (
                  <p className="mt-0.5 text-xs text-ink-500">{obligation.notes}</p>
                )}
              </Td>
              <Td>{OBLIGATION_KIND_LABELS[obligation.kind]}</Td>
              <Td>
                {OBLIGATION_FREQUENCY_LABELS[obligation.frequency]}
                {obligation.dateAdjustment !== 'none' && (
                  <p className="mt-0.5 text-xs text-ink-500">
                    {DATE_ADJUSTMENT_LABELS[obligation.dateAdjustment]}
                  </p>
                )}
              </Td>
              <Td className="text-right tabular-nums">{formatMoney(obligation.amountMinor)}</Td>
              <Td>
                <span className="text-xs text-ink-700">
                  {obligation.upcoming
                    .slice(0, 3)
                    .map((occurrence) => formatDate(occurrence.date))
                    .join(' · ') || 'None in the next 90 days'}
                </span>
              </Td>
              <Td>
                <Badge tone={obligation.confidence === 'known' ? 'positive' : 'warning'}>
                  {obligation.confidence === 'known' ? 'Contracted' : 'Estimated'}
                </Badge>
              </Td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
    </Card>
  )
}

function ObligationForm({ onDone }: { onDone: () => void }) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)

  const [form, setForm] = useState({
    name: '',
    kind: 'payroll' as ObligationKind,
    frequency: 'biweekly' as ObligationFrequency,
    confidence: 'known' as ObligationConfidence,
    amount: '',
    startsOn: '',
    dateAdjustment: 'previous_business_day' as DateAdjustment,
    semiMonthlyDays: '1,15',
    notes: '',
  })

  const create = useMutation({
    mutationFn: async () => {
      /**
       * Dollars are converted to minor units here, once, and the API refuses a
       * value with decimals. A figure typed as dollars and sent as cents is
       * wrong by a factor of a hundred in the direction of understating what
       * the firm owes.
       */
      const dollars = Number.parseFloat(form.amount)
      if (!Number.isFinite(dollars) || dollars <= 0) {
        throw new Error('Enter an amount greater than zero.')
      }

      return apiFetch('/cash-flow/obligations', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name,
          kind: form.kind,
          frequency: form.frequency,
          confidence: form.confidence,
          amountMinor: Math.round(dollars * 100),
          startsOn: form.startsOn,
          dateAdjustment: form.dateAdjustment,
          semiMonthlyDays:
            form.frequency === 'semi_monthly'
              ? form.semiMonthlyDays
                  .split(',')
                  .map((part) => Number.parseInt(part.trim(), 10))
                  .slice(0, 2)
              : null,
          notes: form.notes || null,
        }),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['cash-flow'] })
      onDone()
    },
    onError: (mutationError: Error) => setError(mutationError.message),
  })

  return (
    <CardBody className="border-b border-ink-100 bg-ink-50/60">
      <form
        className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
        onSubmit={(event) => {
          event.preventDefault()
          setError(null)
          create.mutate()
        }}
      >
        <Labelled label="Name">
          <input
            required
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            className={INPUT}
            placeholder="Biweekly payroll"
          />
        </Labelled>

        <Labelled label="Kind">
          <select
            value={form.kind}
            onChange={(event) =>
              setForm((prev) => ({ ...prev, kind: event.target.value as ObligationKind }))
            }
            className={INPUT}
          >
            {OBLIGATION_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {OBLIGATION_KIND_LABELS[kind]}
              </option>
            ))}
          </select>
        </Labelled>

        <Labelled label="Frequency">
          <select
            value={form.frequency}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                frequency: event.target.value as ObligationFrequency,
              }))
            }
            className={INPUT}
          >
            {OBLIGATION_FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {OBLIGATION_FREQUENCY_LABELS[frequency]}
              </option>
            ))}
          </select>
        </Labelled>

        <Labelled label="Amount (dollars)">
          <input
            required
            inputMode="decimal"
            value={form.amount}
            onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))}
            className={INPUT}
            placeholder="84000.00"
          />
        </Labelled>

        <Labelled
          label="First occurrence"
          hint="Every later date is derived from this one, so a fortnightly cycle keeps its phase."
        >
          <input
            required
            type="date"
            value={form.startsOn}
            onChange={(event) => setForm((prev) => ({ ...prev, startsOn: event.target.value }))}
            className={INPUT}
          />
        </Labelled>

        <Labelled label="If it lands on a weekend or holiday">
          <select
            value={form.dateAdjustment}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                dateAdjustment: event.target.value as DateAdjustment,
              }))
            }
            className={INPUT}
          >
            {DATE_ADJUSTMENTS.map((adjustment) => (
              <option key={adjustment} value={adjustment}>
                {DATE_ADJUSTMENT_LABELS[adjustment]}
              </option>
            ))}
          </select>
        </Labelled>

        {form.frequency === 'semi_monthly' && (
          <Labelled label="Days of the month" hint="Two, comma separated — e.g. 1,15 or 15,31.">
            <input
              value={form.semiMonthlyDays}
              onChange={(event) =>
                setForm((prev) => ({ ...prev, semiMonthlyDays: event.target.value }))
              }
              className={INPUT}
            />
          </Labelled>
        )}

        <Labelled
          label="Certainty"
          hint="An estimate is shown differently on the forecast, so one wrong utility bill does not discredit the contracted lines beside it."
        >
          <select
            value={form.confidence}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                confidence: event.target.value as ObligationConfidence,
              }))
            }
            className={INPUT}
          >
            <option value="known">Contracted — the amount is known</option>
            <option value="estimated">Estimated</option>
          </select>
        </Labelled>

        <Labelled label="Note" hint="Where the figure comes from. Shown on every forecast line.">
          <input
            value={form.notes}
            onChange={(event) => setForm((prev) => ({ ...prev, notes: event.target.value }))}
            className={INPUT}
          />
        </Labelled>

        <div className="sm:col-span-2 lg:col-span-3">
          {error && (
            <Alert tone="error" className="mb-3">
              {error}
            </Alert>
          )}
          <Button type="submit" variant="accent" disabled={create.isPending}>
            {create.isPending ? 'Saving…' : 'Save obligation'}
          </Button>
        </div>
      </form>
    </CardBody>
  )
}

const INPUT =
  'mt-1 w-full rounded-md border border-ink-300 px-3 py-2 text-sm text-ink-900 ' +
  'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none'

function Labelled({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <label className="block text-sm">
      <span className="font-medium text-ink-800">{label}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-ink-500">{hint}</span>}
    </label>
  )
}

/* ------------------------------------------------------------------------ *
 * Accuracy
 * ------------------------------------------------------------------------ */

interface AccuracyPayload {
  points: {
    targetDate: string
    horizonDays: number
    projectedConservativeMinor: number
    projectedExpectedMinor: number
    projectedOptimisticMinor: number
    actualClosingMinor: number
    varianceMinor: number
    withinBand: boolean
  }[]
  settledCount: number
  pendingCount: number
  withinBandRateBps: number | null
}

/**
 * How well past forecasts did.
 *
 * KAN-90: "a forecast nobody has back-tested is a guess with a chart". The
 * measure is whether the outcome fell **inside the band**, not how close it came
 * to the expected line — that is the claim the forecast actually made, and
 * scoring it against a point estimate would grade it on a promise it never gave.
 */
function Accuracy() {
  const accuracy = useQuery({
    queryKey: ['cash-flow', 'accuracy'],
    queryFn: () => apiFetch<AccuracyPayload>('/cash-flow/accuracy'),
  })

  const recent = useMemo(
    () => (accuracy.data?.points ?? []).slice(-12).reverse(),
    [accuracy.data]
  )

  return (
    <Card>
      <CardHeader
        title="How this forecast has done"
        description="Each projection is stored when it is made and checked against the balance on the day it was about."
      />

      {accuracy.data && accuracy.data.settledCount === 0 ? (
        <CardBody>
          <p className="text-sm text-ink-600">
            Nothing has come due yet. {accuracy.data.pendingCount} projection
            {accuracy.data.pendingCount === 1 ? ' is' : 's are'} waiting on their target date — a
            hit rate is shown once the first one settles, rather than a nought that would read as
            "always wrong".
          </p>
        </CardBody>
      ) : (
        <>
          {accuracy.data && (
            <CardBody className="border-b border-ink-100">
              <p className="text-sm text-ink-700">
                <strong className="font-semibold text-brand-900">
                  {formatBps(accuracy.data.withinBandRateBps ?? 0)}
                </strong>{' '}
                of {accuracy.data.settledCount} settled projection
                {accuracy.data.settledCount === 1 ? '' : 's'} landed inside the band.
              </p>
            </CardBody>
          )}
          <TableWrap>
            <thead>
              <tr>
                <Th>Target date</Th>
                <Th>Horizon</Th>
                <Th className="text-right">Projected (expected)</Th>
                <Th className="text-right">Actual</Th>
                <Th className="text-right">Variance</Th>
                <Th>Inside the band</Th>
              </tr>
            </thead>
            <tbody>
              {recent.length === 0 && <EmptyRow colSpan={6}>No settled projections.</EmptyRow>}
              {recent.map((point) => (
                <tr key={`${point.targetDate}-${point.horizonDays}`}>
                  <Td>{formatDate(point.targetDate)}</Td>
                  <Td>{point.horizonDays} days</Td>
                  <Td className="text-right tabular-nums">
                    {formatMoney(point.projectedExpectedMinor)}
                  </Td>
                  <Td className="text-right tabular-nums">
                    {formatMoney(point.actualClosingMinor)}
                  </Td>
                  <Td className="text-right tabular-nums">{formatMoney(point.varianceMinor)}</Td>
                  <Td>
                    <Badge tone={point.withinBand ? 'positive' : 'warning'}>
                      {point.withinBand ? 'Yes' : 'No'}
                    </Badge>
                  </Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </>
      )}
    </Card>
  )
}
